import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, Box, DialogContentText,
  Alert, Typography
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format, addMinutes } from 'date-fns';
import TimeSlotSelector from './TimeSlotSelector';
import { useTheme } from '@mui/material/styles';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import { getClient } from '../../api/companyRecords';
import { getCompany } from '../../api/company/company';
import { upsertAppointment } from '../../api/appointments';

const combineDateTime = (date, time) => {
  if (!date || !time) return null;
  
  const timeMatch = time.toLowerCase().match(/(\d+):(\d+)\s*(am|pm)/);
  if (!timeMatch) return null;

  const result = new Date(date);
  let [_, hours, minutes, period] = timeMatch;
  hours = parseInt(hours);
  minutes = parseInt(minutes);

  // Convert to 24-hour format
  if (period === 'pm' && hours !== 12) {
    hours += 12;
  } else if (period === 'am' && hours === 12) {
    hours = 0;
  }

  result.setHours(hours, minutes, 0, 0);
  return result;
};

const formatDateTime = (dateTime) => {
  if (!dateTime) return 'No date available';
  
  try {
    const date = dateTime instanceof Date
      ? dateTime
      : dateTime?.toDate && typeof dateTime.toDate === 'function'
        ? dateTime.toDate()
        : new Date(dateTime);
    
    return format(date, 'EEEE, MMMM d, yyyy • h:mm a');
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'No date available';
  }
};

const RescheduleDialog = ({ 
  open, 
  onClose, 
  appointment, 
  onReschedule,
  onError
}) => {
  const theme = useTheme();
  const { user, companyData } = useAuth();
  const [selectedDateTime, setSelectedDateTime] = useState(null);
  const [companyHours, setCompanyHours] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const { setNotification } = useNotification();
  const [clientDetails, setClientDetails] = useState(null);

  // Move the memoized service object here
  const memoizedService = useMemo(() => ({
    id: appointment?.serviceId,
    duration: appointment?.duration,
    ...appointment?.serviceTags?.[0]
  }), [appointment]);

  // Set service once when appointment changes
  useEffect(() => {
    if (appointment?.serviceTags?.[0]) {
      setSelectedService({
        id: appointment.serviceId,
        duration: appointment.duration,
        ...appointment.serviceTags[0]
      });
    }
  }, [appointment]);

  // Fetch company data and hours
  const fetchCompanyData = useCallback(async () => {
    if (!user?.companyId) return;

    try {
      setIsLoading(true);
      const company = await getCompany({ companyId: user.companyId });
      const hours = company?.settings?.companyHours;
      if (!hours) return;

      const formatTime = (time) => time ? String(time).toUpperCase() : null;
      const formattedHours = {};
      Object.entries(hours).forEach(([day, dayHours]) => {
        formattedHours[day] = {
          ...dayHours,
          open: formatTime(dayHours.open),
          close: formatTime(dayHours.close),
          breakStart: formatTime(dayHours.breakStart),
          breakEnd: formatTime(dayHours.breakEnd)
        };
      });

      setCompanyHours(formattedHours);
    } catch (error) {
      console.error('Error fetching company data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.companyId]);

  const resetState = useCallback(() => {
    setSelectedDateTime(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setSelectedService(null);
    setIsLoading(false);
  }, []);

  const handleClose = () => {
    resetState();
    onClose();
  };

  useEffect(() => {
    if (open) {
      resetState();
      fetchCompanyData();
    }
  }, [open, resetState, fetchCompanyData]);

  const handleTimeSelect = useCallback((slot) => {
    if (!slot) return;
    
    if (slot.dateTime) {
      requestAnimationFrame(() => {
        setSelectedTime(slot.displayTime);
        setSelectedDateTime(slot.dateTime);
      });
    } else {
      const dateTime = combineDateTime(selectedDate, slot.time);
      requestAnimationFrame(() => {
        setSelectedTime(slot.time);
        setSelectedDateTime(dateTime);
      });
    }
  }, [selectedDate]);

  const handleReschedule = async (newDateTime) => {
    if (!newDateTime || !appointment) return;

    try {
      const durationMinutes = Number(appointment.duration) > 0 ? Number(appointment.duration) : 60;
      const now = new Date().toISOString();
      const previousDateTime = appointment.dateTime instanceof Date
        ? appointment.dateTime.toISOString()
        : appointment.dateTime;
      const historyEntry = {
        from: previousDateTime,
        to: newDateTime.toISOString(),
        at: now,
        by: {
          userId: user.uid,
          name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Staff',
          role: user.role
        }
      };

      await upsertAppointment({
        companyId: user.companyId,
        id: appointment.id,
        appointment: {
          dateTime: newDateTime.toISOString(),
          endTime: addMinutes(newDateTime, durationMinutes).toISOString(),
          lastUpdated: now,
          rescheduleStatus: 'rescheduled',
          status: appointment.status || 'confirmed',
          originalDateTime: appointment.originalDateTime || previousDateTime,
          rescheduledDateTime: newDateTime.toISOString(),
          rescheduledAt: now,
          rescheduledBy: historyEntry.by,
          reminderSent: false,
          reminderSMSSent: false,
          automatedCheckInReminderSent: false,
          rescheduleHistory: Array.isArray(appointment.rescheduleHistory)
            ? [...appointment.rescheduleHistory, historyEntry]
            : [historyEntry],
        },
      });

      setNotification({
        open: true,
        message: 'Appointment rescheduled successfully!',
        severity: 'success',
        autoHideDuration: 3000
      });

      onReschedule(newDateTime);
      resetState();
      onClose();
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      onError?.(error.message);
      
      setNotification({
        open: true,
        message: 'Failed to reschedule appointment',
        severity: 'error',
        autoHideDuration: 3000
      });
    }
  };

  const isBusinessDay = (date) => {
    const dayOfWeek = format(date, 'EEEE').toLowerCase();
    return companyHours[dayOfWeek]?.isOpen === true;
  };

  const shouldDisableDate = (date) => !isBusinessDay(date);

  const handleConfirmReschedule = () => {
    setConfirmationOpen(true);
  };

  const handleCancelConfirmation = () => {
    setConfirmationOpen(false);
  };

  // Add client details fetch
  useEffect(() => {
    const fetchClientDetails = async () => {
      if (!appointment?.clientId || !user?.companyId) return;
      
      try {
        const clientData = await getClient({
          companyId: user.companyId,
          id: appointment.clientId,
        });
        
        if (clientData) {
          setClientDetails(clientData);
        }
      } catch (error) {
        console.error('Error fetching client details:', error);
      }
    };

    fetchClientDetails();
  }, [appointment?.clientId, user?.companyId]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          pb: 2,
          px: 3
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="h6" fontWeight={600}>
              Reschedule Appointment
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {clientDetails?.firstName} {clientDetails?.lastName}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Current Appointment Card */}
            <Box sx={{ 
              bgcolor: 'primary.lighter',
              p: 2,
              borderRadius: 2,
              display: 'flex',
              gap: 2,
              alignItems: 'flex-start'
            }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" color="primary.main" fontWeight={600} gutterBottom>
                  Current Appointment
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDateTime(appointment?.dateTime)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {appointment?.serviceTags?.[0]?.name || 'No service'} • {appointment?.duration} min
                </Typography>
              </Box>
            </Box>

            {/* Date and Time Selection */}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr',
                gap: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}>
                <DatePicker
                  label="Select New Date"
                  value={selectedDate}
                  onChange={(newValue) => {
                    if (newValue && !isNaN(newValue.getTime())) {
                      setSelectedDate(newValue);
                      setSelectedTime(null);
                    }
                  }}
                  shouldDisableDate={shouldDisableDate}
                  disablePast
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "medium",
                      variant: "outlined"
                    }
                  }}
                />

                {selectedDate && (
                  <Box sx={{ 
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 2
                  }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Available Times
                    </Typography>
                    <TimeSlotSelector
                      key={selectedDate.toISOString()}
                      selectedDate={selectedDate}
                      companyHours={companyHours}
                      onTimeSelect={handleTimeSelect}
                      companyId={user?.companyId}
                      selectedTime={selectedDateTime}
                      selectedService={memoizedService}
                      excludeAppointmentId={appointment?.id}
                    />
                  </Box>
                )}

                {/* Selected New Time Preview */}
                {selectedDateTime && (
                  <Box sx={{ 
                    bgcolor: 'success.lighter',
                    p: 2,
                    borderRadius: 2,
                    mt: 1
                  }}>
                    <Typography variant="subtitle2" color="success.main" gutterBottom>
                      New Appointment Time
                    </Typography>
                    <Typography variant="body2" color="success.dark">
                      {formatDateTime(selectedDateTime)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </LocalizationProvider>
          </Box>
        </DialogContent>

        <DialogActions sx={{ 
          px: 3, 
          py: 2,
          borderTop: '1px solid rgba(0,0,0,0.08)',
          gap: 1 
        }}>
          <Button 
            onClick={handleClose}
            sx={{ 
              borderRadius: 2,
              px: 3,
              textTransform: 'none'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmReschedule}
            disabled={!selectedDateTime || !selectedTime}
            variant="contained"
            color="primary"
            sx={{ 
              borderRadius: 2,
              px: 3,
              textTransform: 'none'
            }}
          >
            Reschedule
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmationOpen}
        onClose={handleCancelConfirmation}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          }
        }}
      >
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Confirm New Time
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Are you sure you want to reschedule this appointment?
          </Typography>
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mt: 2
          }}>
            <Box sx={{ 
              bgcolor: 'error.lighter',
              p: 1.5,
              borderRadius: 1
            }}>
              <Typography variant="caption" color="error.main">
                From: {formatDateTime(appointment?.dateTime)}
              </Typography>
            </Box>
            <Box sx={{ 
              bgcolor: 'success.lighter',
              p: 1.5,
              borderRadius: 1
            }}>
              <Typography variant="caption" color="success.main">
                To: {formatDateTime(selectedDateTime)}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={handleCancelConfirmation}
            sx={{ 
              borderRadius: 2,
              px: 3,
              textTransform: 'none'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={async () => {
              setConfirmationOpen(false);
              await handleReschedule(selectedDateTime);
            }}
            variant="contained"
            color="primary"
            sx={{ 
              borderRadius: 2,
              px: 3,
              textTransform: 'none'
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RescheduleDialog;